import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { useToast } from "@/shared/ui/use-toast";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import TagsService, { LanguageCode, TagTranslation, TagUpdateData, type CreateTagData } from "@/features/tags/service/tagService";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { X, Plus, Languages, Eye, EyeOff, Save } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import { Separator } from "@/shared/ui/separator";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { useTranslation } from "@/shared/i18n";

type FormData = {
    published: boolean;
    isCategory: boolean;
    translations: TagTranslation[];
};

const LANGUAGES = [
    { code: LanguageCode.PT_BR, label: "Português", flag: "🇧🇷" },
    { code: LanguageCode.EN, label: "English", flag: "🇺🇸" },
    { code: LanguageCode.ES, label: "Español", flag: "🇪🇸" },
];

export default function CreateUpdateTag() {
    const { t } = useTranslation('tags');
    const navigate = useNavigate();
    const { id } = useParams();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const isEditMode = !!id;
    const { tag: currentTag } = useLocation().state || {};

    const { control, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<FormData>({
        defaultValues: {
            published: currentTag?.published || true,
            isCategory: currentTag?.isCategory || false,
            translations: currentTag?.translations || [
                { languageCode: LanguageCode.PT_BR, name: "" },
            ],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "translations",
    });

    const watchedTranslations = watch("translations");
    const watchedPublished = watch("published");

    const { mutate: createTag, isPending: isCreating } = useCustomMutation({
        serviceInstance: TagsService.Instance,
        selectAction: (s) => s.create,
        onSuccess: () => {
            toast({
                title: `✅ ${t('success')}`,
                description: t('tagCreated'),
            });
            navigate("/tags");
        },
        onError: () => {
            toast({
                title: `❌ ${t('error')}`,
                description: t('errorCreating'),
                variant: "destructive"
            });
        }
    });

    const { mutate: updateTag, isPending: isUpdating } = useCustomMutation({
        serviceInstance: TagsService.Instance,
        selectAction: (s) => s.update,
        onSuccess: () => {
            toast({
                title: `✅ ${t('success')}`,
                description: t('tagUpdated'),
            });
            navigate("/tags");
        },
        onError: () => {
            toast({
                title: `❌ ${t('error')}`,
                description: t('errorUpdating'),
                variant: "destructive"
            });
        }
    });

    const isPending = isCreating || isUpdating;

    const onSubmit = (data: FormData) => {
        // Validar se há pelo menos uma tradução preenchida
        const validTranslations = data.translations.filter(t => t.name.trim() !== "");
        if (validTranslations.length === 0) {
            toast({
                title: `⚠️ ${t('attention')}`,
                description: t('addValidTranslation'),
                variant: "destructive"
            });
            return;
        }
        console.log(data);
        if (isEditMode && id) {
            const tagUpdateData: TagUpdateData = {
                published: data.published,
                isCategory: data.isCategory,
                translations: data.translations.map(t => ({
                    languageCode: t.languageCode,
                    name: t.name,
                })),
                replaceTranslations: true,
            };
            updateTag({ id, data: tagUpdateData });
        } else {
            createTag({ ...data } as CreateTagData);
        }
    };

    const getAvailableLanguages = () => {
        const usedLanguages = watchedTranslations.map(t => t.languageCode);
        return LANGUAGES.filter(lang => !usedLanguages.includes(lang.code));
    };

    const addTranslation = (languageCode: LanguageCode) => {
        append({ languageCode, name: "" });
    };

    const getLanguageInfo = (code: LanguageCode) => {
        return LANGUAGES.find(lang => lang.code === code) || { label: code, flag: "🌐" };
    };

    if (isLoading) {
        return (
            <PageContent title={t('loading')}>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center space-y-4">
                        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="text-neutral-400">{t('loadingTagData')}</p>
                    </div>
                </div>
            </PageContent>
        );
    }

    return (
        <PageContent
            title={isEditMode ? t('editTag') : t('newTag')}
            items={[
                { label: t('tags'), href: "/tags" },
                { label: isEditMode ? t('editTag') : t('newTag'), href: "#" },
            ]}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="flex flex-wrap gap-2">
                    <Badge variant={watchedPublished ? "default" : "secondary"} className="gap-1 text-white">
                        {watchedPublished ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {watchedPublished ? t('published') : t('draft')}
                    </Badge>
                    {watchedTranslations.length > 0 && (
                        <Badge variant="outline" className="gap-1 text-white">
                            <Languages className="w-3 h-3" />
                            {watchedTranslations.filter(t => t.name.trim() !== "").length} {t('languages')}
                        </Badge>
                    )}
                </div>

                <Card className="bg-gradient-to-br from-neutral-900 to-neutral-800 border-neutral-700">
                    <CardHeader className="pb-4">
                        <CardTitle className="flex items-center gap-2 text-neutral-100">
                            <Languages className="w-5 h-5 text-blue-400" />
                            {t('translations')}
                        </CardTitle>
                        <p className="text-sm text-neutral-400">
                            {t('translationsDescription')}
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {fields.length === 0 && (
                            <Alert className="border-amber-500/50 bg-amber-950/20">
                                <AlertDescription className="text-amber-300">
                                    {t('addTranslationAlert')}
                                </AlertDescription>
                            </Alert>
                        )}

                        {fields.map((field, index) => {
                            const langInfo = getLanguageInfo(watchedTranslations[index]?.languageCode);
                            return (
                                <div
                                    key={field.id}
                                    className="group relative bg-neutral-800/50 backdrop-blur-sm border border-neutral-700/50 rounded-xl p-4 hover:border-neutral-600/50 transition-all duration-200"
                                >
                                    <div className="flex items-center gap-3">
                                        <Controller
                                            control={control}
                                            name={`translations.${index}.languageCode`}
                                            render={({ field }) => (
                                                <Select value={field.value} onValueChange={field.onChange}>
                                                    <SelectTrigger className="w-[160px] bg-neutral-900/80 border-neutral-600 text-white hover:border-neutral-500 transition-colors">
                                                        <SelectValue className="text-white">
                                                            <div className="flex items-center gap-2 text-white">
                                                                <span className="text-base">{langInfo.flag}</span>
                                                                <span className="text-sm text-white">{langInfo.label}</span>
                                                            </div>
                                                        </SelectValue>
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-neutral-900 border-neutral-700">
                                                        {LANGUAGES.map((lang) => (
                                                            <SelectItem
                                                                key={lang.code}
                                                                value={lang.code}
                                                                className="hover:bg-neutral-800 focus:bg-neutral-800 text-white"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-base text-white">{lang.flag}</span>
                                                                    <span className="text-white">{lang.label}</span>
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />

                                        {/* Name Input */}
                                        <Controller
                                            control={control}
                                            name={`translations.${index}.name`}
                                            rules={{ required: t('nameRequired') }}
                                            render={({ field, fieldState }) => (
                                                <div className="flex-1">
                                                    <Input
                                                        {...field}
                                                        onChange={(e) => field.onChange(e.target.value.toLowerCase().trim().replace(/\s+/g, "_"))}
                                                        placeholder={t('tagNamePlaceholder', { language: langInfo.label })}
                                                        className="bg-neutral-900/80 border-neutral-600 text-neutral-100 placeholder-neutral-500 hover:border-neutral-500 focus:border-blue-500 transition-colors"
                                                    />
                                                    {fieldState.error && (
                                                        <p className="text-red-400 text-xs mt-1">
                                                            {fieldState.error.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />

                                        {/* Remove Button */}
                                        {fields.length > 1 && (
                                            <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                className="text-red-400 hover:text-red-300 hover:bg-red-950/30 opacity-0 group-hover:opacity-100 transition-all duration-200"
                                                onClick={() => remove(index)}
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Add Language Buttons */}
                        {getAvailableLanguages().length > 0 && (
                            <div>
                                <Separator className="bg-neutral-700 my-4" />
                                <div className="flex flex-wrap gap-2">
                                    {getAvailableLanguages().slice(0, 4).map((lang) => (
                                        <Button
                                            key={lang.code}
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="bg-neutral-800/50 border-neutral-600 text-neutral-300 hover:bg-neutral-700 hover:text-neutral-100 transition-colors"
                                            onClick={() => addTranslation(lang.code)}
                                        >
                                            <span className="mr-1">{lang.flag}</span>
                                            {lang.label}
                                        </Button>
                                    ))}
                                    {getAvailableLanguages().length > 4 && (
                                        <Select onValueChange={addTranslation}>
                                            <SelectTrigger className="w-auto bg-neutral-800/50 border-neutral-600 text-neutral-300 hover:bg-neutral-700">
                                                <Plus className="w-3 h-3 mr-1" />
                                                {t('moreLanguages')}
                                            </SelectTrigger>
                                            <SelectContent className="bg-neutral-900 border-neutral-700">
                                                {getAvailableLanguages().slice(4).map((lang) => (
                                                    <SelectItem
                                                        key={lang.code}
                                                        value={lang.code}
                                                        className="hover:bg-neutral-800"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span>{lang.flag}</span>
                                                            <span>{lang.label}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Settings Card */}
                <Card className="bg-neutral-900/50 border-neutral-700">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-neutral-100">{t('settings')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="flex items-center justify-between p-4 bg-neutral-800/30 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {watchedPublished ? <Eye className="w-5 h-5 text-green-400" /> : <EyeOff className="w-5 h-5 text-neutral-400" />}
                                    <div>
                                        <Label htmlFor="published" className="text-neutral-200 font-medium">
                                            {t('publishedLabel')}
                                        </Label>
                                        <p className="text-xs text-neutral-400">
                                            {watchedPublished ? t('publishedDescription') : t('draftDescription')}
                                        </p>
                                    </div>
                                </div>
                                <Controller
                                    control={control}
                                    name="published"
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                            id="published"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-neutral-700">
                    <div />
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            className="border-neutral-600 text-neutral-300 bg-neutral-800 "
                            onClick={() => navigate("/tags")}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isPending || (!isDirty && isEditMode)}
                            className="bg-gradient-to-r from-[#D91657] to-[#D91657] hover:from-[#D91657] hover:to-[#D91657] text-white shadow-lg shadow-[#D91657]/20 disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
                        >
                            {isPending ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    {t('saving')}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Save className="w-4 h-4" />
                                    {isEditMode ? t('update') : t('createTag')}
                                </div>
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </PageContent>
    );
}